import React, { useEffect, useRef, useCallback, useState } from 'react'
import PropTypes from 'prop-types'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import DivButton from 'GeneralUI/DivButton/DivButton.jsx'
import { useDispatch } from 'react-redux'

const SpinnerErrors = ({ config, closeHandler = () => null, clearHandler = () => null, startSpinner }) => {
  const timerRef = useRef(null)
  const [acknowledged, setAcknowledged] = useState(false)
  const { autoCloseDelaySec, autoClearDelaySec, response } = config

  useEffect(() => {
    // componentDidMount
    if (autoCloseDelaySec === 0) {
      closeHandler()
    } else if (autoCloseDelaySec) {
      timerRef.current = setTimeout(closeHandler, autoCloseDelaySec * 1000)
    } else if (autoClearDelaySec === 0) {
      clearHandler()
    } else if (autoClearDelaySec) {
      timerRef.current = setTimeout(clearHandler, autoClearDelaySec * 1000)
    }
    return () => {
      // component will unmount
      clearTimeout(timerRef.current)
    }
  }, [])

  const errorBuilder = useCallback((errors) => {
    const overrideClick = () => {
      const ack = Object.keys(response.data.confirmation_required).every(override => document.getElementById(override)?.checked)
      setAcknowledged(ack)
    }
    const recursiveBuilder = (subErrors, key = '', override = false) => {
      if (typeof subErrors === 'string') {
        return override ? (<li><input type="checkbox" id={key} onClick={overrideClick}/>{subErrors}</li>) : (<li>- {subErrors}</li>)
      } else if (Array.isArray(subErrors)) {
        return subErrors.map((item, idx) => {
          return (<ul key={`${key}_${idx}`}>{recursiveBuilder(item, key, override)}</ul>)
        })
      } else if (typeof subErrors === 'object') {
        return Object.keys(subErrors).map((type) => {
          return (
            <ul key={`${key}_idx`}>
              {`${type}:`}
              {recursiveBuilder(subErrors[type], type, type.indexOf('override_') !== -1)}
            </ul>
          )
        })
      }
    }

    return (
        <div
          className="error-box alert alert-danger"
        >
          <div className="errors" >
            {recursiveBuilder(errors)}
          </div>
        </div>
    )
  }, [acknowledged])

  let { errors = 'An error has occurred', action } = config

  // check for known error types that should be handled automatically
  if (typeof response?.data == 'string') {
    if (response.data.includes('privilege_error:')) {
      // override the error message to be the appropriate privilege error.
      errors = response.data
    }
  } else if (typeof response?.data === 'object' && response?.data?.confirmation_required) {
    errors = response.data.confirmation_required
    const modifiedActionObj = { ...(action.obj || []) }
    Object.keys(errors).forEach(confirmation => {
      modifiedActionObj[confirmation] = true
    })

    // overide bottom content to add continue/cancel buttons
    config.bottomContent = (closeHandler, clearHandler) =>
      <OverrideButtons
        closeHandler={closeHandler}
        clearHandler={clearHandler}
        acknowledged={acknowledged}
        startSpinner={startSpinner}
        action = {{ ...action, obj: modifiedActionObj }}
      />
  }

  const message = config.message
    ? <div className="message">
    {config.message}
  </div>
    : null
  return (
    <div
      id="spinner-errors"
    >
      <div className="wrapper">
        {message}
        <div
          className="error animate__animated animate__rotateIn"
        >
          <FontAwesomeIcon icon={['fas', 'exclamation-triangle']} size="1x" />
        </div>
        {errorBuilder(errors)}
        {config.bottomContent ? config.bottomContent(closeHandler, clearHandler) : null}
      </div>
    </div>
  )
}

const OverrideButtons = ({ closeHandler, clearHandler, action, acknowledged = false, startSpinner }) => {
  const dispatch = useDispatch()
  const continueHandler = (standardHandler) => {
    standardHandler()
    startSpinner()
    dispatch(action)
  }
  return (
    <div className='spinner-bottom-content'>
      <ClearButton closeHandler={closeHandler} clearHandler={clearHandler} buttonText="Cancel"/>
      <ClearButton disabled = {!acknowledged} closeHandler={() => continueHandler(closeHandler)} clearHandler={() => continueHandler(clearHandler)} buttonText="Continue"/>
    </div>
  )
}

const ClearButton = ({ closeHandler, clearHandler, buttonText = 'Clear Error', disabled }) => {
  return (
    <
      DivButton
      className = 'btn btn-default'
      disabled={disabled}
      onClick={closeHandler}
    >
      {buttonText}
    </DivButton>
  )
}

SpinnerErrors.propTypes = {
  errors: PropTypes.oneOfType([
    PropTypes.array,
    PropTypes.object,
    PropTypes.string
  ])
}

export default SpinnerErrors
